// Canonical scoring function. This is THE source of truth for point calculation —
// the backend uses it to lock in points when a match ends; the frontend uses it
// for "what-if" previews.
//
// Rules (from PLAN.md §6):
//   per stage (FT / ET / Pens), points are awarded by MUTUALLY EXCLUSIVE tiers:
//     - exact score match      → 3
//     - correct sign only      → 1
//     - neither                → 0
//
//   stages are scored only if the match actually reached them AND the user
//   submitted a prediction for that stage. For penalties, "draw" is not a
//   possible sign — the sign is which team wins the shootout.

import type { MatchResult, MatchStage, PredictionPayload, Score, ScoringResult } from './types';

export type Sign = 'team1' | 'draw' | 'team2';

export function sign(s: Score): Sign {
  if (s.team1 > s.team2) return 'team1';
  if (s.team1 < s.team2) return 'team2';
  return 'draw';
}

export function isExact(a: Score, b: Score): boolean {
  return a.team1 === b.team1 && a.team2 === b.team2;
}

export function isKnockoutStage(stage: MatchStage): boolean {
  return stage !== 'group';
}

/**
 * Which side won a finished knockout match: full-time decides; if drawn, extra
 * time; if still drawn, penalties. Returns null only for an (impossible) still-
 * level completed knockout. Robust to extraTime being stored cumulatively or as
 * the ET period alone — the SIGN of the deciding stage is the winner either way.
 */
export function knockoutWinnerSide(result: MatchResult): 'team1' | 'team2' | null {
  const ft = sign(result.fullTime);
  if (ft !== 'draw') return ft;
  if (result.extraTime) {
    const et = sign(result.extraTime);
    if (et !== 'draw') return et;
  }
  if (result.penalties) {
    const pens = sign(result.penalties);
    if (pens !== 'draw') return pens;
  }
  return null;
}

function scoreStage(prediction: Score, actual: Score): number {
  if (isExact(prediction, actual)) return 3;
  if (sign(prediction) === sign(actual)) return 1;
  return 0;
}

export function scoreMatch(
  prediction: PredictionPayload,
  actual: MatchResult,
  isKnockout: boolean,
): ScoringResult {
  const breakdown = { fullTime: 0, extraTime: 0, penalties: 0 };

  // Full time is scored always.
  breakdown.fullTime = scoreStage(prediction.fullTime, actual.fullTime);

  // ET is scored only if the match actually went to ET AND the user submitted
  // an ET prediction (which they can only do by first predicting FT draw).
  if (isKnockout && actual.extraTime && prediction.extraTime) {
    breakdown.extraTime = scoreStage(prediction.extraTime, actual.extraTime);
  }

  // Penalties likewise. Pens has no draw sign — winner is determined by which
  // team scored more, which `sign()` returns naturally.
  if (isKnockout && actual.penalties && prediction.penalties) {
    breakdown.penalties = scoreStage(prediction.penalties, actual.penalties);
  }

  const total = breakdown.fullTime + breakdown.extraTime + breakdown.penalties;
  return { total, breakdown };
}
